import type { TWellListView } from 'src/api-types/wells.types';
import { di } from 'src/packages/di';
import { assert } from 'src/packages/shared/utils/assert';
import { removeBracesFromString } from 'src/packages/shared/utils/removeBracesFromString';
import { TemplateValueBuilder } from 'src/packages/template-builder/TemplateBuilder';
import type { IDataSource } from 'src/packages/template-builder/TemplateBuilder';
import type { LocationNameConfigurator } from 'src/packages/user-preference/UserPreference.api';
import { WellItemEntity } from 'src/pages/dashboard/features/well-list-widget/WellCollapse';
import type { WellCollapseEntity } from 'src/pages/dashboard/features/well-list-widget/WellCollapse';

import { WellsCollapseEntity } from './WellsCollapse.entity';

type TWellsResponseType = Record<string, string | number | Record<string, string | number>[]>[];

export class CollapseEntityDataSource implements IDataSource {
  constructor(private readonly source: { [key: string]: string | number | Record<string, string | number>[] }) {}

  hasKey(key: string): boolean {
    return key in this.source;
  }

  getValue(key: string) {
    assert(key in this.source);

    const value = this.source[key];

    assert(typeof value !== 'undefined');

    return value;
  }
}

function isWellsCollapses(collapses: object, itemsKey: string): collapses is TWellsResponseType {
  if (!Array.isArray(collapses)) return false;

  for (const collapse of collapses) {
    if (!collapse || collapse === null || typeof collapse !== 'object') return false;
    if (!(itemsKey in collapse)) return false;

    const items = collapse[itemsKey];

    if (!Array.isArray(items)) return false;

    for (const item of items) {
      if (typeof item !== 'object') return false;
    }
  }

  return true;
}

function isLocationNameConfiguration(
  customNameTemplate?: Record<string, unknown>,
): customNameTemplate is TWellListView['grouping']['groups'][number]['collapse']['customNameTemplate'] {
  return (
    typeof customNameTemplate === 'object' &&
    customNameTemplate !== null &&
    typeof customNameTemplate.name === 'string' &&
    customNameTemplate.name === 'locationNameConfiguration'
  );
}

function getLocationNameConfiguration(
  customNameTemplate: TWellListView['grouping']['groups'][number]['collapse']['customNameTemplate'],
  userConfigurator: LocationNameConfigurator,
  collapseEntityDataSource: CollapseEntityDataSource,
): string {
  if (!customNameTemplate) return '';

  const { fields } = customNameTemplate;

  return Object.entries(userConfigurator)
    .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))
    .map(([key, field]) => {
      if (field.order === null) return null;
      const dataKeys = fields[key];
      const prop = dataKeys[field.displayName as keyof typeof dataKeys];
      return collapseEntityDataSource.hasKey(prop) ? collapseEntityDataSource.getValue(prop) : '';
    })
    .filter(Boolean)
    .join(' / ');
}

export function mapWells(collapses: object, view: TWellListView, groupBy: number): WellCollapseEntity[] {
  const currentGroupViewData = view.grouping.groups.find((_, index) => index === groupBy)?.collapse;

  if (!currentGroupViewData) return [];

  const { itemsKey } = currentGroupViewData;

  if (isWellsCollapses(collapses, itemsKey)) {
    const collapseNameKey = currentGroupViewData.nameTemplate;

    const { nameTemplate: itemNameTemplate, idKey_Favorites: itemIdKey } = currentGroupViewData.item;

    const mappedCollapses: WellCollapseEntity[] = [];

    for (const collapse of collapses) {
      const source = new CollapseEntityDataSource(collapse);
      let collapseName: string | {} | null = '';
      if (isLocationNameConfiguration(currentGroupViewData.customNameTemplate)) {
        const userConfigurator = di.requireService('userPreference').preferences.locationNameConfigurator;
        collapseName = getLocationNameConfiguration(currentGroupViewData.customNameTemplate, userConfigurator, source);
      } else {
        const templateValueBuilder = new TemplateValueBuilder().configure((settings) => settings.useDataSource(source));
        collapseName = templateValueBuilder.build(collapseNameKey);
      }
      const collapseAttrName = collapseNameKey.includes('.')
        ? removeBracesFromString(collapseNameKey).split('.')[1]
        : collapseNameKey;

      if (typeof collapseName !== 'string' || !(itemsKey in collapse)) {
        continue;
      }

      const items = collapse[itemsKey];

      if (!Array.isArray(items)) {
        continue;
      }

      const mappedCollapseItems: WellItemEntity[] = [];

      for (const item of items) {
        const source = new CollapseEntityDataSource(item);
        const templateValueBuilder = new TemplateValueBuilder().configure((settings) => settings.useDataSource(source));

        const itemName = templateValueBuilder.build(itemNameTemplate);
        const id = item[itemIdKey];
        const status = item[view.item.statusField];

        if (typeof itemName !== 'string' || typeof id !== 'number' || typeof status !== 'string') continue;

        mappedCollapseItems.push(new WellItemEntity({ name: itemName, id, status }));
      }

      mappedCollapses.push(
        new WellsCollapseEntity({
          attrName: collapseAttrName,
          name: collapseName,
          items: mappedCollapseItems,
          useCollapseTranslation: currentGroupViewData.useCollapseTranslation,
        }),
      );
    }

    return mappedCollapses;
  }

  return [];
}
